import { siteApi } from "@api";
import { toast } from "@components/utils/Toaster";
import { SEON_FEATURE } from "@configs/seon";
import { useSeonSession } from "@hooks/useSeonSession";
import { resetSkinnerBoxData } from "@stores/skinnerBox/slices";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { APIErrorCodes, WalletTxMethod, useHandleApiResponse } from "kz-ui-sdk";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { SKINNER_BOX_STATE } from "../utils";

type AnimationElement = "background" | "sparkle" | "content" | "number" | "button" | "coin";

interface UseSkinnerBoxStateReturn {
  state: SKINNER_BOX_STATE;
  clickLocked: boolean;
  isLoading: boolean;
  setState: (state: SKINNER_BOX_STATE) => void;
  setClickLocked: (locked: boolean) => void;
  guardClick: () => boolean;
  handleClaimClick: () => void;
  showClaim: boolean;
  isClaimed: boolean;
  showSparkles: boolean;
  sparklesDirection: "in" | "out";
  layerZIndexes: Record<AnimationElement, number>;
}

interface UseSkinnerBoxStateCallbacks {
  onClaimSuccess?: () => void;
}

export const useSkinnerBoxState = (
  zIndex: number = 10,
  callbacks?: UseSkinnerBoxStateCallbacks,
): UseSkinnerBoxStateReturn => {
  const dispatch = useDispatch();
  const [state, setState] = useState<SKINNER_BOX_STATE>(SKINNER_BOX_STATE.IDLE);
  const [clickLocked, setClickLocked] = useState(false);
  const [isRequestingClaim, setIsRequestingClaim] = useState(false);

  const { t } = useTranslation();
  const [claimReward, claimResult] = siteApi.useClaimRewardMutation();
  const { getSession: getSeonSession, isFetching: isFetchingSeonSession } = useSeonSession(SEON_FEATURE.PROMOTION);
  const { handleApiResponse } = useHandleApiResponse({
    toast,
  });

  const guardClick = useCallback(() => clickLocked, [clickLocked]);

  const handleClaimClick = useCallback(async () => {
    if (guardClick()) return;
    if (state !== SKINNER_BOX_STATE.CLAIMABLE && state !== SKINNER_BOX_STATE.REVEAL) return;

    setClickLocked(true);
    setIsRequestingClaim(true);

    try {
      // Get SEON session
      const seonSession = await getSeonSession();

      // Call claim API
      const claimRewardRes = await claimReward({
        seonSession,
        query: {
          type: WalletTxMethod.SkinnerBox,
        },
      });

      handleApiResponse(claimRewardRes, {
        onSuccess: () => {
          // Success: Set state to claimed
          setState(SKINNER_BOX_STATE.CLAIMED);
          // Notify caller that claim request succeeded (to start any fallback timers)
          callbacks?.onClaimSuccess?.();
        },
        onError: () => {
          setClickLocked(false);
        },
        errorMessageResolver: (error) => {
          if (error?.data?.error?.message === APIErrorCodes.ClaimReward.SkinnerBoxNotClaimable) {
            dispatch(resetSkinnerBoxData());
            return t("already claimed");
          }
          return resolveAuthResponseError(error, t);
        },
      });
    } catch (error) {
      // Handle any unexpected errors
      toast.error(t("an error occurred, please try again"));
      setClickLocked(false);
    } finally {
      setIsRequestingClaim(false);
    }
  }, [
    guardClick,
    state,
    setClickLocked,
    setState,
    getSeonSession,
    claimReward,
    handleApiResponse,
    t,
    dispatch,
    callbacks,
  ]);

  // Layer z-indexes for animation elements
  const layerZIndexes = useMemo<Record<AnimationElement, number>>(() => {
    return {
      background: zIndex + 1,
      sparkle: zIndex,
      content: zIndex + 2,
      number: zIndex + 3,
      button: zIndex + 5,
      coin: zIndex + 4,
    };
  }, [zIndex]);

  // Derived states
  const showClaim = state === SKINNER_BOX_STATE.CLAIMABLE;
  const isClaimed = state === SKINNER_BOX_STATE.CLAIMED;
  const showSparkles = state === SKINNER_BOX_STATE.CLAIMABLE || state === SKINNER_BOX_STATE.CLAIMED;
  const sparklesDirection = state === SKINNER_BOX_STATE.CLAIMED ? "in" : ("out" as const);

  // Update loading state based on API call
  const isLoading = isRequestingClaim || isFetchingSeonSession || claimResult.isLoading;

  return {
    state,
    clickLocked,
    isLoading,
    setState,
    setClickLocked,
    guardClick,
    handleClaimClick,
    showClaim,
    isClaimed,
    showSparkles,
    sparklesDirection,
    layerZIndexes,
  };
};
